(function () {

  console.log('[ImageSwitcher] 🚀 Script loaded');

  let _correctImageUrl  = null;
  let _correctZoomUrl   = null;
  let _correctAltText   = null;
  let _observer         = null;
  let _isUpdating       = false;
  let _thumbClicked     = false;
  let _slickSettings    = null;
  let _prefetched       = false;
  let _mode             = null; // 'multi' or 'single', set during init

  // Option-memory state — see the OPTION MEMORY section near the bottom.
  let _wantedOptions    = null;
  let _restoringOptions = false;
  let _optionPollTimer  = null;
  let _optionDeadline    = 0;

  // ─── STAND-DOWN FLAG ─────────────────────────────────────────────────────────
  // assets/vdb-variant-media.js renders the gallery from per-variant metafield
  // images and is authoritative whenever window.vdbVariantMedia exists. This
  // script's alt-text matching, slickFilter pass and src MutationObserver would
  // actively revert those images, so all image work is disabled in that case.
  //
  // What stays live: syncVariantId() / updatePriceDisplay(), and the option
  // memory below. Those are still needed because app.js updateData() only
  // updates `.product-actions .special-price`, which does not match the visible
  // price at vdb-product-page-design-default.liquid:467.
  const _mediaOwnedElsewhere = !!window.vdbVariantMedia;

  // Option wrappers, used by both the dropdown listeners and the option memory.
  const SHAPE_WRAP = '.option-supported-shapes';
  const METAL_WRAP = '.option-metal-color';
  const CARAT_WRAP = '.option-head-sizes';

  // ─── MODE DETECTION ──────────────────────────────────────────────────────────
  // Multi-option products (shape + metal + carat, e.g. engagement ring settings)
  // render BOTH a shape select and a carat/head-size select. Single-option
  // products (e.g. plain metal-only bands) render neither.
  //
  // NOTE: an earlier version of this detector checked for the vdb_mini_search
  // tag, but that turned out to be a global site widget present on every page
  // (including single-option products), so it always resolved to 'multi'.
  // Detecting on the shape/carat selects themselves is reliable because their
  // presence is exactly what distinguishes the two product types.
  function detectMode() {
    const hasShapeSelect = !!document.querySelector(SHAPE_WRAP + ' select');
    const hasCaratSelect = !!document.querySelector(CARAT_WRAP + ' select');

    const mode = (hasShapeSelect && hasCaratSelect) ? 'multi' : 'single';
    console.log('[ImageSwitcher] 🔍 shape select ' + (hasShapeSelect ? 'found' : 'not found') +
      ', carat select ' + (hasCaratSelect ? 'found' : 'not found') + ' → mode =', mode);
    return mode;
  }

  // ─── READ CURRENT DROPDOWN SELECTION ───────────────────────────────────────
  function getSelectedCombo() {
    if (_mode === 'multi') {
      const shape = document.querySelector(SHAPE_WRAP + ' select')?.value;
      const metal = document.querySelector(METAL_WRAP + ' select')?.value;
      const carat = document.querySelector(CARAT_WRAP + ' select')?.value;
      return { shape, metal, carat };
    }
    // single mode: only metal matters
    const metal = document.querySelector(METAL_WRAP + ' select')?.value;
    return { shape: null, metal, carat: null };
  }

  function slugifyMetal(metal) {
    return metal ? metal.toLowerCase().replace(/\s+/g, '-') : null;
  }

  // ─── GET PRODUCT OPTIONS OBJECT DYNAMICALLY ─────────────────────────────────
  function getProductOptionsObject() {
    const optionsKey = Object.keys(window).find(key => key.startsWith('product_options_'));
    if (optionsKey) return window[optionsKey];

    const productIdInput = document.querySelector('input[name="product-id"]');
    if (productIdInput) {
      const varName = 'product_options_' + productIdInput.value;
      if (window[varName]) return window[varName];
    }

    const form = document.querySelector('form[id^="product_form_"]');
    if (form) {
      const varName = 'product_options_' + form.id.replace('product_form_', '');
      if (window[varName]) return window[varName];
    }

    console.error('[ImageSwitcher] ❌ Could not find product_options_XXXX object');
    return null;
  }

  // ─── FETCH VARIANT DATA ──────────────────────────────────────────────────────
  function getVariantData(shape, metal, carat) {
    const productOptions = getProductOptionsObject();
    if (!productOptions) return null;

    if (_mode === 'multi') {
      if (!productOptions[shape]?.[metal]?.[carat]) {
        console.error('[ImageSwitcher] ❌ No variant found at productOptions["' + shape + '"]["' + metal + '"]["' + carat + '"]. Actual object shape:', productOptions);
        return null;
      }
      return productOptions[shape][metal][carat];
    }

    // single mode: flat lookup keyed by metal
    if (!productOptions[metal]) {
      console.error('[ImageSwitcher] ❌ No variant found at productOptions["' + metal + '"]. Actual object shape:', productOptions);
      return null;
    }
    return productOptions[metal];
  }

  // ─── FORMAT PRICE ────────────────────────────────────────────────────────────
  function formatPrice(cents) {
    const amount = (cents / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ─── UPDATE PRICE IN DOM ─────────────────────────────────────────────────────
  function updatePriceDisplay(variantData) {
    if (!variantData) return;

    const price          = variantData.price;
    const compareAtPrice = variantData.compare_at_price;
    const formattedPrice = formatPrice(price);

    document.querySelectorAll('.special-price').forEach(el => {
      el.textContent = formattedPrice + ' USD';
    });

    const compareSelectors = [
      '.product-single__price--compare',
      '.price__compare',
      '[data-compare-price]',
      '.compare-at-price',
    ];

    compareSelectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        if (compareAtPrice) {
          el.textContent = formatPrice(compareAtPrice);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    });
  }

  // ─── UPDATE HIDDEN INPUT [name="id"] ────────────────────────────────────────
  function updateVariantInput(variantId) {
    if (!variantId) return false;

    const variantInput = document.querySelector('input[name="id"]')
                      || document.querySelector('[name="id"]');
    if (!variantInput) return false;

    if (variantInput.value === String(variantId)) return true;

    const oldValue = variantInput.value;
    variantInput.value = variantId;
    console.log('[ImageSwitcher] 🔄 Variant input updated:', oldValue, '→', variantId);

    ['change', 'input'].forEach(type => {
      variantInput.dispatchEvent(new Event(type, { bubbles: true }));
    });

    document.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: { variantId }
    }));

    return true;
  }

  // ─── SYNC VARIANT ID + PRICE ─────────────────────────────────────────────────
  function syncVariantId(shape, metal, carat) {
    const variantData = getVariantData(shape, metal, carat);
    if (!variantData) {
      console.error('[ImageSwitcher] ❌ Variant data not found');
      return;
    }
    updateVariantInput(variantData['id']);
    updatePriceDisplay(variantData);
  }

  // ─── PREFETCH ALL GALLERY IMAGES (warms browser cache) ──────────────────────
  function prefetchAllImages() {
    if (_mediaOwnedElsewhere) return;
    if (_prefetched) return;
    _prefetched = true;

    const anchors = document.querySelectorAll('#previewsGallery > a[data-image]');
    console.log('[ImageSwitcher] 📥 Prefetching', anchors.length, 'gallery images');

    anchors.forEach(a => {
      const url = a.getAttribute('data-image');
      if (url) {
        const img = new Image();
        img.src = url.replace(/^\/\//, 'https://');
      }
    });
  }

  // ─── STORE ORIGINAL SLICK SETTINGS ─────────────────────────────────────────
  function captureSlickSettings() {
    if (typeof $ === 'undefined') return;
    const $gallery = $('#previewsGallery');
    if ($gallery.hasClass('slick-initialized')) {
      _slickSettings = $gallery.slick('getSlick').options;
    }
  }

  // ─── FALLBACK SLICK INIT ─────────────────────────────────────────────────────
  // On some product templates the theme's own inline script throws before it
  // reaches its Slick initialization call (e.g. a null .addEventListener on a
  // selector that only exists on multi-option products), so the gallery never
  // gets the slick-initialized class or its thumbnail styling. Rather than
  // depend on that script being fixed, initialize Slick ourselves if it's
  // still missing by the time we're ready to filter slides.
  function ensureSlickInitialized() {
    if (_mediaOwnedElsewhere) return;
    if (typeof $ === 'undefined') return;
    const $gallery = $('#previewsGallery');
    if (!$gallery.length) return;
    if ($gallery.hasClass('slick-initialized')) return;

    console.warn('[ImageSwitcher] ⚠️ Slick not initialized by theme — initializing fallback carousel');
    try {
      $gallery.slick({
        slidesToShow: 4,
        slidesToScroll: 1,
        infinite: false,
        arrows: true,
        dots: false
      });
      console.log('[ImageSwitcher] ✅ Fallback Slick init complete');
    } catch (e) {
      console.error('[ImageSwitcher] ❌ Fallback Slick init failed:', e);
    }
  }

  // ─── ALT-TEXT MATCHING ───────────────────────────────────────────────────────
  // multi mode: alt is a single hyphenated key like "14k-white-gold-cushion-1ct"
  //   → compare as a word-set (metal's internal spaces converted to hyphens
  //     first, since "14K White Gold" must become "14k-white-gold" to line up
  //     with the alt slug).
  // single mode: alt is a comma-separated list like
  //   "14k-white-gold,18k-white-gold,platinum" → exact-item match, not substring
  //   (avoids "white" or "gold" wrongly matching a different metal).
  function altMatchesSelection(altText, shape, metal, carat) {
    if (!altText) return false;
    const metalSlug = slugifyMetal(metal);

    if (_mode === 'single') {
      return altText
        .toLowerCase()
        .split(',')
        .map(s => s.trim())
        .includes(metalSlug);
    }

    // multi mode
    const keyWords = `${metalSlug}-${shape}-${carat}`.toLowerCase().split('-').filter(Boolean);
    const slideWords = altText.toLowerCase().split('-').filter(Boolean);
    if (keyWords.length !== slideWords.length) return false;
    return keyWords.every(w => slideWords.includes(w));
  }

  // ─── FILTER GALLERY SLIDES BASED ON SELECTION ───────────────────────────────
  function filterGallerySlides() {
    if (_mediaOwnedElsewhere) return;
    const { shape, metal, carat } = getSelectedCombo();
    if (!metal) return;
    if (_mode === 'multi' && (!shape || !carat)) return;
    if (typeof $ === 'undefined') return;

    const $gallery = $('#previewsGallery');
    if (!$gallery.length) return;

    const matchFn = function () {
      const img = this.querySelector('img');
      if (!img || !img.alt) return false;
      return altMatchesSelection(img.alt, shape, metal, carat);
    };

    if ($gallery.hasClass('slick-initialized')) {
      try { $gallery.slick('slickUnfilter'); } catch (e) {}
      $gallery.slick('slickFilter', matchFn);
      console.log('[ImageSwitcher] 🔄 Gallery filtered via slickFilter');
    } else {
      $gallery.find('> a').toArray().forEach(function (item) {
        const img = item.querySelector('img');
        item.style.display = (img && altMatchesSelection(img.alt, shape, metal, carat)) ? '' : 'none';
      });
    }

    document.querySelectorAll('.thumbnails-wrapper-mobile a').forEach(function (thumb) {
      const img = thumb.querySelector('img');
      thumb.style.display = (img && altMatchesSelection(img.alt, shape, metal, carat)) ? '' : 'none';
    });
  }

  // ─── FIND MATCHING SLIDE ────────────────────────────────────────────────────
  function findMatchingSlide(shape, metal, carat) {
    const slides = document.querySelectorAll('#previewsGallery .slick-slide:not([style*="display: none"]):not([style*="display:none"])');
    const allItems = slides.length > 0
      ? slides
      : document.querySelectorAll('#previewsGallery > a[style=""], #previewsGallery > a:not([style])');

    for (const slide of allItems) {
      const img = slide.querySelector('img');
      if (img && altMatchesSelection(img.alt, shape, metal, carat)) return slide;
    }

    console.warn('[ImageSwitcher] ❌ No slide match for:', { shape, metal, carat });
    return null;
  }

  // ─── APPLY ZOOM + LINKS ──────────────────────────────────────────────────────
  function applyZoomAndLinks(mainImg, fullZoom) {
    const zoomLink = document.querySelector('#main_image_container .zoom-link');
    if (zoomLink) zoomLink.href = fullZoom;

    const zoomBGImage = document.querySelector('#main_image_container .zoomWindowContainer');
    if (zoomBGImage) {
      zoomBGImage.style.backgroundImage = `url('${fullZoom}')`;
      const innerDiv = zoomBGImage.querySelector('div');
      if (innerDiv) innerDiv.style.backgroundImage = `url('${fullZoom}')`;
    }

    if (typeof $ !== 'undefined') {
      try {
        const $img = $(mainImg);
        $img.trigger('zoom.destroy');
        $img.zoom({ url: fullZoom });
      } catch (e) {
        console.warn('[ImageSwitcher] ⚠️ Zoom plugin error:', e.message);
      }
    }
  }

  // ─── CORE: APPLY IMAGE TO DOM (with preloading) ─────────────────────────────
  function applyImageToDom(imageUrl, zoomUrl, altText) {
    if (!imageUrl) return;

    const fullSrc  = imageUrl.replace(/^\/\//, 'https://');
    const fullZoom = (zoomUrl || imageUrl).replace(/^\/\//, 'https://');

    const mainImg = document.querySelector('#main_image_container .zoom')
                 || document.querySelector('#main_image_container img');
    if (!mainImg) return;
    if (mainImg.src === fullSrc) return;

    const doSwap = function () {
      _isUpdating = true;
      mainImg.setAttribute('src', fullSrc);
      mainImg.src = fullSrc;
      if (altText) mainImg.alt = altText;
      applyZoomAndLinks(mainImg, fullZoom);
      console.log('[ImageSwitcher] 🖼️ Image applied:', fullSrc);
      setTimeout(function () { _isUpdating = false; }, 100);
    };

    const pre = new Image();
    pre.onload  = doSwap;
    pre.onerror = doSwap;
    pre.src = fullSrc;

    if (pre.complete) doSwap();
  }

  // ─── START MUTATION OBSERVER ────────────────────────────────────────────────
  function startObserver() {
    if (_mediaOwnedElsewhere) return;
    const mainImg = document.querySelector('#main_image_container .zoom')
                 || document.querySelector('#main_image_container img');
    if (!mainImg) return;

    if (_observer) _observer.disconnect();

    _observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName !== 'src') return;
        if (_isUpdating)   return;
        if (_thumbClicked) return;

        const newSrc = mainImg.getAttribute('src');
        if (_correctImageUrl) {
          const fullCorrect = _correctImageUrl.replace(/^\/\//, 'https://');
          if (newSrc !== fullCorrect) {
            console.log('[ImageSwitcher] 🔁 Theme reset detected — restoring correct image');
            applyImageToDom(_correctImageUrl, _correctZoomUrl, _correctAltText);
          }
        }
      });
    });

    _observer.observe(mainImg, { attributes: true, attributeFilter: ['src'] });
    console.log('[ImageSwitcher] 👁️ Observer started');
  }

  // ─── ATTACH THUMBNAIL CLICK LISTENERS ───────────────────────────────────────
  function attachThumbnailListeners() {
    if (_mediaOwnedElsewhere) return;
    const gallery = document.querySelector('#previewsGallery');
    if (gallery) {
      gallery.addEventListener('click', function (e) {
        const slide = e.target.closest('.slick-slide, a[data-image]');
        if (!slide) return;

        _thumbClicked = true;

        const clickedImage = slide.getAttribute('data-image')
                          || slide.querySelector('img')?.src;
        const clickedZoom  = slide.getAttribute('data-zoom-image') || clickedImage;
        const clickedAlt   = slide.querySelector('img')?.alt || '';

        if (clickedImage) {
          _correctImageUrl = clickedImage;
          _correctZoomUrl  = clickedZoom;
          _correctAltText  = clickedAlt;
        }

        setTimeout(function () { _thumbClicked = false; }, 500);
      });
    } else {
      console.warn('[ImageSwitcher] ⚠️ #previewsGallery not found for thumb listeners');
    }

    const mobileThumbs = document.querySelector('.thumbnails-wrapper-mobile');
    if (mobileThumbs) {
      mobileThumbs.addEventListener('click', function (e) {
        const thumb = e.target.closest('a[data-image]');
        if (!thumb) return;

        _thumbClicked = true;

        const clickedImage = thumb.getAttribute('data-image');
        const clickedZoom  = thumb.getAttribute('data-zoom-image') || clickedImage;
        const clickedAlt   = thumb.querySelector('img')?.alt || '';

        if (clickedImage) {
          _correctImageUrl = clickedImage;
          _correctZoomUrl  = clickedZoom;
          _correctAltText  = clickedAlt;
        }

        setTimeout(function () { _thumbClicked = false; }, 500);
      });
    }

    console.log('[ImageSwitcher] 👂 Thumbnail click listeners attached');
  }

  // ─── MAIN UPDATE ────────────────────────────────────────────────────────────
  function updateImageFromSelection() {
    const { shape, metal, carat } = getSelectedCombo();

    if (!metal || (_mode === 'multi' && (!shape || !carat))) {
      console.error('[ImageSwitcher] ❌ Missing dropdown value(s)');
      return;
    }

    // 1. Sync variant ID + price
    syncVariantId(shape, metal, carat);

    // 2. Image work belongs to vdb-variant-media.js when it is present.
    if (_mediaOwnedElsewhere) return;

    // Find matching slide and update image
    const slide = findMatchingSlide(shape, metal, carat);
    if (!slide) return;

    const imageUrl = slide.getAttribute('data-image');
    const zoomUrl  = slide.getAttribute('data-zoom-image');
    const imgEl    = slide.querySelector('img');
    const altText  = imgEl?.alt || metal;

    _correctImageUrl = imageUrl;
    _correctZoomUrl  = zoomUrl;
    _correctAltText  = altText;

    applyImageToDom(imageUrl, zoomUrl, altText);
    setTimeout(() => applyImageToDom(imageUrl, zoomUrl, altText), 200);
  }

  // ─── OPTION MEMORY ──────────────────────────────────────────────────────────
  // Preserves the shopper's metal (and carat) choice across a SHAPE change.
  //
  // The theme's data is nested product_options_<id>[shape][metal][carat], so
  // when the shape select changes the theme repopulates the metal and carat
  // selects from the keys available under the new shape — and re-selects the
  // FIRST/default key rather than the one the shopper had chosen. The variant
  // id, the price and the gallery all then follow that reset value, which is
  // why picking metal → shape appears to revert to the previous metal.
  //
  // rememberOptions() is registered in the CAPTURE phase at the bottom of this
  // IIFE (not inside init()) so it runs before the theme's own handler on the
  // shape select, while the metal/carat values are still the shopper's.
  //
  // If the new shape genuinely doesn't offer the remembered metal, nothing is
  // restored and the theme's default stands — that is the correct behaviour.

  const OPTION_WATCH_MS  = 2500; // how long to wait for the rebuild to settle
  const OPTION_TICK_MS   = 50;
  const OPTION_SETTLE_MS = 120;  // after re-applying, before we re-sync media

  function optionSelect(wrapper) {
    const wrap = document.querySelector(wrapper);
    return wrap ? wrap.querySelector('select') : null;
  }

  function optionValue(wrapper) {
    const el = optionSelect(wrapper);
    return el ? el.value : null;
  }

  // Values can come back with different casing/whitespace after a rebuild,
  // so fall back to a loose match before giving up.
  function findOptionValue(el, value) {
    if (!el || value == null || value === '') return null;
    for (let i = 0; i < el.options.length; i++) {
      if (el.options[i].value === value) return el.options[i].value;
    }
    const target = String(value).trim().toLowerCase();
    for (let i = 0; i < el.options.length; i++) {
      if (String(el.options[i].value).trim().toLowerCase() === target) {
        return el.options[i].value;
      }
    }
    return null;
  }

  function applyWantedOptions() {
    if (!_wantedOptions) return false;

    let last = null;

    [[METAL_WRAP, _wantedOptions.metal], [CARAT_WRAP, _wantedOptions.carat]].forEach(pair => {
      const el = optionSelect(pair[0]);
      if (!el) return;
      const match = findOptionValue(el, pair[1]);
      if (!match || el.value === match) return;
      console.log('[ImageSwitcher] ♻️ Restoring', pair[0], el.value, '→', match);
      el.value = match;
      last = el;
    });

    if (!last) return false;

    // One bubbling change is enough: the theme reads every select when it
    // recalculates, and firing on both would run the whole pipeline twice.
    _restoringOptions = true;
    last.dispatchEvent(new Event('change', { bubbles: true }));

    setTimeout(function () {
      _restoringOptions = false;
      resyncAfterRestore();
    }, OPTION_SETTLE_MS);

    return true;
  }

  function resyncAfterRestore() {
    // Re-asserts variant id + price from the current select values...
    updateImageFromSelection();
    if (!_mediaOwnedElsewhere) filterGallerySlides();

    // ...then forces the metafield gallery to match that variant id.
    if (window.VdbVariantMedia && typeof window.VdbVariantMedia.sync === 'function') {
      try { window.VdbVariantMedia.sync(true); }
      catch (e) { console.warn('[ImageSwitcher] ⚠️ VdbVariantMedia.sync failed:', e.message); }
    }
  }

  // The selects are the single source of truth. If input[name="id"] drifted
  // (e.g. a handler read the dropdowns mid-rebuild), put it back.
  function reconcileVariantId() {
    const { shape, metal, carat } = getSelectedCombo();
    if (!metal || (_mode === 'multi' && (!shape || !carat))) return;

    const data = getVariantData(shape, metal, carat);
    if (!data || !data.id) return;

    const input = document.querySelector('input[name="id"]')
               || document.querySelector('[name="id"]');
    if (!input || input.value === String(data.id)) return;

    console.warn('[ImageSwitcher] ⚠️ Variant id did not match the visible options —',
      input.value, '→', data.id);
    updateVariantInput(data.id);
    updatePriceDisplay(data);
    resyncAfterRestore();
  }

  function optionMemoryTick() {
    if (_restoringOptions) {
      _optionPollTimer = setTimeout(optionMemoryTick, OPTION_TICK_MS);
      return;
    }

    applyWantedOptions();

    if (Date.now() < _optionDeadline) {
      _optionPollTimer = setTimeout(optionMemoryTick, OPTION_TICK_MS);
      return;
    }

    _optionPollTimer = null;
    _wantedOptions = null;
    reconcileVariantId();
  }

  function startOptionWatch() {
    _optionDeadline = Date.now() + OPTION_WATCH_MS;
    if (_optionPollTimer) return;
    _optionPollTimer = setTimeout(optionMemoryTick, OPTION_TICK_MS);
  }

  function rememberOptions(event) {
    const target = event.target;
    if (!target || target.tagName !== 'SELECT') return;
    if (_restoringOptions) return;
    if (!target.closest(SHAPE_WRAP)) return;

    _wantedOptions = { metal: optionValue(METAL_WRAP), carat: optionValue(CARAT_WRAP) };
    console.log('[ImageSwitcher] 🧠 Shape changed — remembering', _wantedOptions);
    startOptionWatch();
  }

  // ─── PATCH setPrice() ───────────────────────────────────────────────────────
  function patchSetPrice() {
    if (typeof window.setPrice === 'function') {
      window._originalSetPrice = window.setPrice;
      window._patchedSetPrice  = true;
      window.setPrice = function () {
        window._originalSetPrice.apply(this, arguments);
        setTimeout(updateImageFromSelection, 30);
      };
      console.log('[ImageSwitcher] ✅ setPrice() patched');
    } else {
      setTimeout(patchSetPrice, 200);
    }
  }

  // ─── PATCH updateData() ─────────────────────────────────────────────────────
  function patchUpdateData() {
    if (typeof window.updateData === 'function') {
      const _original = window.updateData;
      window.updateData = function () {
        _original.apply(this, arguments);
        setTimeout(updateImageFromSelection, 30);
      };
      console.log('[ImageSwitcher] ✅ updateData() patched');
    } else {
      console.warn('[ImageSwitcher] ⚠️ updateData() not found');
    }
  }

  // ─── ATTACH DROPDOWN LISTENERS ──────────────────────────────────────────────
  function attachDropdownListeners() {
    const selectors = _mode === 'multi'
      ? [SHAPE_WRAP + ' select', METAL_WRAP + ' select', CARAT_WRAP + ' select']
      : [METAL_WRAP + ' select'];

    selectors.forEach(sel => {
      const el = document.querySelector(sel);
      if (el) {
        el.addEventListener('change', function (e) {
          console.log('[ImageSwitcher] 🖱️ Dropdown changed:', sel, '→', e.target.value);
          _thumbClicked = false;
          updateImageFromSelection();
          filterGallerySlides();
        });
      } else {
        console.error('[ImageSwitcher] ❌ Not found:', sel);
      }
    });
  }

  // ─── INIT ────────────────────────────────────────────────────────────────────
  function init() {
    _mode = detectMode();

    patchSetPrice();
    patchUpdateData();
    attachDropdownListeners();
    attachThumbnailListeners();
    startObserver();

    setTimeout(function () {
      ensureSlickInitialized();
      captureSlickSettings();
      prefetchAllImages();
      updateImageFromSelection();
      filterGallerySlides();
      startObserver();
    }, 600);
  }

  // Registered here rather than in init(): init() is deferred (DOMContentLoaded,
  // or +300ms when the DOM is already parsed), and this listener has to exist
  // before the shopper can touch the shape select. It only reads values, so
  // running before _mode is set is harmless.
  document.addEventListener('change', rememberOptions, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 300);
  }

  // ─── EXPOSE DEBUG TOOLS ──────────────────────────────────────────────────────
  window.ImageSwitcherDebug = {
    update:        updateImageFromSelection,
    filterGallery: filterGallerySlides,
    prefetch:      prefetchAllImages,
    startWatch:    startObserver,
    getMode:       () => _mode,
    getStored:     () => ({ url: _correctImageUrl, zoom: _correctZoomUrl, alt: _correctAltText }),
    getVariant:    () => document.querySelector('input[name="id"]')?.value,
    lookupId:      (shape, metal, carat) => getVariantData(shape, metal, carat),
    testPrice:     (shape, metal, carat) => updatePriceDisplay(getVariantData(shape, metal, carat)),
    remembered:    () => _wantedOptions,
    reconcile:     reconcileVariantId,
  };

})();

(function () {

  // Fallback sticky-header handler.
  //
  // On pages where the theme's own inline script throws before it finishes
  // running (see the null .addEventListener crash on multi-vs-single-option
  // product templates), whatever code normally toggles the sticky classes on
  // scroll never gets registered. The header keeps its base
  // "sticky always stickylight" classes but never gains "is-sticky animated
  // slideInDown" on scroll.
  //
  // This script is a no-op if the theme's own listener is already working —
  // adding the same classes twice has no visible effect — so it's safe to
  // include on every product page, not just the broken ones.

  const SCROLL_THRESHOLD = 50; // px scrolled before the header goes "sticky"
  const STICKY_CLASSES = ['is-sticky', 'animated', 'slideInDown'];

  function getHeader() {
    return document.querySelector('header.page-header') || document.querySelector('header');
  }

  function applyStickyState() {
    const header = getHeader();
    if (!header) return;

    const shouldBeSticky = window.scrollY > SCROLL_THRESHOLD;
    const isSticky = header.classList.contains('is-sticky');

    if (shouldBeSticky && !isSticky) {
      header.classList.add(...STICKY_CLASSES);
    } else if (!shouldBeSticky && isSticky) {
      header.classList.remove(...STICKY_CLASSES);
    }
  }

  function init() {
    const header = getHeader();
    if (!header) {
      console.warn('[StickyHeaderFallback] ⚠️ No header element found');
      return;
    }

    // Only step in if the header doesn't support sticky behavior at all in
    // the first place (e.g. this template genuinely doesn't want it).
    if (!header.classList.contains('sticky')) {
      console.log('[StickyHeaderFallback] Header not configured for sticky — skipping');
      return;
    }

    window.addEventListener('scroll', applyStickyState, { passive: true });
    applyStickyState(); // run once in case the page loads already scrolled

    console.log('[StickyHeaderFallback] ✅ Fallback scroll listener attached');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();